import { api } from './client';
import type { School } from '../types/api';

export const schoolsApi = {
  // List all schools with optional filtering.
  // By default Closed/Inactive schools are excluded; pass includeClosed=true
  // to include them. Has no effect when operationalStatus is explicitly set.
  async list(params?: {
    skip?: number;
    limit?: number;
    search?: string;
    leaId?: number;
    operationalStatus?: string;
    includeClosed?: boolean;
  }): Promise<School[]> {
    const queryParams = new URLSearchParams();
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.leaId) queryParams.append('leaId', params.leaId.toString());
    if (params?.operationalStatus) queryParams.append('operationalStatus', params.operationalStatus);
    if (params?.includeClosed) queryParams.append('includeClosed', 'true');

    const query = queryParams.toString();
    return api.get<School[]>(`/schools${query ? `?${query}` : ''}`);
  },

  // Get a single school by ID
  async get(schoolId: number): Promise<School> {
    return api.get<School>(`/schools/${schoolId}`);
  },

  // Create a new school
  async create(school: Partial<School>): Promise<School> {
    return api.post<School>(`/schools`, school);
  },

  // Update a school
  async update(schoolId: number, updates: Partial<School>): Promise<School> {
    return api.put<School>(`/schools/${schoolId}`, updates);
  },

  // Delete a school (soft delete - marks as Closed; preserves historical record)
  async delete(schoolId: number): Promise<void> {
    return api.delete(`/schools/${schoolId}`);
  },

  // Reopen a previously-closed school (flip operational status back to Active).
  // Returns 400 if the school is not currently Closed/Inactive.
  async reopen(schoolId: number): Promise<void> {
    return api.post(`/schools/${schoolId}/reopen`);
  },

  // Permanently delete a school (hard delete - removes row and owned children).
  // Refuses with 409 if dependents exist; caller should surface response.detail.blockers.
  async deletePermanent(schoolId: number): Promise<void> {
    return api.delete(`/schools/${schoolId}/permanent`);
  },

  // Get school staff
  async getStaff(schoolId: number): Promise<any[]> {
    return api.get<any[]>(`/schools/${schoolId}/staff`);
  },

  // Add staff to school
  async addStaff(schoolId: number, staffData: any): Promise<any> {
    return api.post<any>(`/schools/${schoolId}/staff`, staffData);
  },

  // Update staff assignment
  async updateStaff(schoolId: number, assignmentId: number, updates: any): Promise<any> {
    return api.put<any>(`/schools/${schoolId}/staff/${assignmentId}`, updates);
  },

  // Remove staff from school
  async removeStaff(schoolId: number, assignmentId: number): Promise<void> {
    return api.delete(`/schools/${schoolId}/staff/${assignmentId}`);
  },

  // Address Management
  async addAddress(schoolId: number, addressData: any): Promise<any> {
    return api.post<any>(`/schools/${schoolId}/addresses`, addressData);
  },

  async updateAddress(schoolId: number, addressId: number, updates: any): Promise<any> {
    return api.put<any>(`/schools/${schoolId}/addresses/${addressId}`, updates);
  },

  async removeAddress(schoolId: number, addressId: number): Promise<void> {
    return api.delete(`/schools/${schoolId}/addresses/${addressId}`);
  }
};